import { useMemo } from 'react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useScrapingQueue } from '@/hooks/useJobs';
import type { QueueStats } from '@repo/types';

// The queue tracks four terminal/active states; map each to a labelled bar.
const STAGES: { key: keyof QueueStats; label: string; indicator: string }[] = [
  { key: 'pending', label: 'Queued', indicator: 'bg-chart-3' },
  { key: 'processing', label: 'Processing', indicator: 'bg-chart-1' },
  { key: 'completed', label: 'Completed', indicator: 'bg-primary' },
  { key: 'failed', label: 'Failed', indicator: 'bg-destructive' },
];

export function ScrapingPipeline() {
  const { data, isLoading, isError } = useScrapingQueue();

  const stages = useMemo(() => {
    const stats = data?.stats;
    if (!stats) return [];
    return STAGES.map((s) => ({ ...s, count: Number(stats[s.key]) || 0 }));
  }, [data]);

  const max = Math.max(1, ...stages.map((s) => s.count));
  const total = stages.reduce((sum, s) => sum + s.count, 0);

  return (
    <Card className="lg:h-[340px]">
      <CardHeader>
        <CardTitle>Scraping Pipeline</CardTitle>
        <CardDescription>Jobs currently at each processing stage</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-2 w-full" />
            </div>
          ))
        ) : isError ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Couldn’t load pipeline stages.
          </p>
        ) : total === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Nothing in the scraping queue.
          </p>
        ) : (
          stages.map((stage) => (
            <div key={stage.key} className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{stage.label}</span>
                <span className="text-muted-foreground tabular-nums">{stage.count}</span>
              </div>
              <Progress
                value={(stage.count / max) * 100}
                className="h-2 bg-muted"
                indicatorClassName={stage.indicator}
              />
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
